import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import routes from '../config/routes'
import userService from '../services/user.service'
import Link from 'next/link'
import onlyGuest from '../hoc/onlyGuest'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo } from 'next-seo'
import { HeroIcons } from '../lib/utils/heroIcons'
import LanguageSelect from '../components/LanguageSelect'
import { SubmitHandler, useForm } from 'react-hook-form'
import useLocale from '../hooks/useLocale'
import axios, { AxiosError } from 'axios'
import { useAuth } from '../providers/AuthProvider'
import { classNames } from '../lib/utils/classNames'
import { getLocalePathFromPathname } from '../lib/utils/language'
import { GoogleTagManagerService } from '../services/google-tag-manager.service'

type FormValues = {
  email: string
  password: string
  passwordConfirm: string
  acceptConditions: boolean
  invitationSlug?: string
}

function RegisterPage() {
  const { t } = useTranslation('register')
  const router = useRouter()
  const { locale } = useLocale()
  const { authWithGoogle } = useAuth()
  const invitationSlugFromQuery =
    typeof router.query.invitationSlug === 'string'
      ? router.query.invitationSlug
      : undefined
  const cgpToggle =
    typeof router.query.cgp === 'string' ? router.query.cgp : undefined
  const showCgpInvitationCard =
    cgpToggle === '1' ||
    cgpToggle?.toLowerCase() === 'true' ||
    Boolean(invitationSlugFromQuery)

  const [isLoading, setLoading] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur' })

  const password = watch('password')
  const invitationSlugInput = watch('invitationSlug')

  useEffect(() => {
    if (!invitationSlugFromQuery) return
    setValue('invitationSlug', invitationSlugFromQuery)
  }, [invitationSlugFromQuery, setValue])

  const validatePassword = (value: string) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/
    if (regex.test(value)) {
      return true
    }
    return t('errors.passwordNotCompliant')
  }

  const validatePasswordConfirm = (value: string) => {
    if (value === password) {
      return true
    }
    return t('errors.passwordNotAlike')
  }

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    setShowPassword(false)
    if (!data || isLoading) return

    const resolvedInvitationSlug =
      invitationSlugFromQuery || data.invitationSlug?.trim() || undefined

    try {
      setLoading(true)
      setSubmitError(null)
      await userService.registerWithEmail(
        data.email,
        data.password,
        resolvedInvitationSlug,
      )
      GoogleTagManagerService.Signup({ method: 'password' })
      await router.push(routes.registerSuccess)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError

        if (axiosError.response && axiosError.response.status == 409) {
          setSubmitError(t('errors.emailAlreadyExists')) // Custom message for 409 error
        } else {
          setSubmitError(t('errors.other'))
        }
      } else {
        setSubmitError(t('errors.other'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />
      <div className="flex min-h-screen items-center bg-purpleGradient px-3 py-10">
        <div className="flex min-h-full flex-1 flex-col justify-center sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img
              className="mx-auto h-8 w-auto"
              src="/img/logos/shelters/logotype-white.svg"
              alt="Shelters"
            />
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
            <div className="py-2">
              <Link href={routes.index}>
                <div className="flex font-bold text-neutralWhite">
                  <HeroIcons.ArrowLeftIcon
                    className="mr-2 h-5 w-5 items-center pt-0.5"
                    aria-hidden="true"
                  />
                  {t('backToMainMenu')}
                </div>
              </Link>
            </div>
            <div className="rounded-lg bg-white px-6 py-6 shadow sm:px-12">
              <div className="pb-6">
                <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-neutralBlack">
                  {t('title')}
                </h2>
              </div>

              <div className="group relative flex">
                <button
                  type="button"
                  className="border-1 flex w-full items-center justify-center gap-3 rounded-md border bg-gray-50/30 px-3 py-1.5 text-white hover:bg-gray-100/30"
                  onClick={() => {
                    const resolvedInvitationSlug =
                      invitationSlugFromQuery ||
                      invitationSlugInput?.trim() ||
                      undefined

                    authWithGoogle({
                      register: true,
                      invitationSlug: resolvedInvitationSlug,
                    })
                  }}
                >
                  <img src="/img/google.svg" width={20} alt="Google logo" />
                  <span className="text-sm font-semibold leading-6 text-neutralBlack">
                    Google
                  </span>
                </button>
              </div>

              <div className="relative mt-6">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-white px-6 font-semibold">{t('or')}</span>
                </div>
              </div>

              <form
                className="mt-6 space-y-6"
                onSubmit={handleSubmit(onSubmit)}
                name="RegisterApp"
              >
                {showCgpInvitationCard && (
                  <div className="rounded-md border border-indigo-100 bg-indigo-50 px-4 py-3">
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-indigo-900">
                        {t('cgpInvitation.title')}
                      </p>
                      <p className="text-xs text-indigo-800">
                        {t('cgpInvitation.subtitle')}
                      </p>
                    </div>

                    <label
                      htmlFor="invitationSlug"
                      className="block text-sm font-semibold leading-6 text-gray-900"
                    >
                      {t('cgpInvitation.slugLabel')}
                    </label>
                    <div className="mt-2">
                      <input
                        className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-indigo-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm sm:leading-6"
                        type="text"
                        placeholder={t('cgpInvitation.slugPlaceholder')}
                        id="invitationSlug"
                        autoComplete="off"
                        disabled={Boolean(invitationSlugFromQuery)}
                        {...register('invitationSlug')}
                      />
                    </div>

                    <p className="mt-1 text-xs text-indigo-800">
                      {invitationSlugFromQuery
                        ? t('cgpInvitation.lockedFromLink')
                        : t('cgpInvitation.help')}
                    </p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    {t('inputs.email')}
                  </label>
                  <div className="mt-2">
                    <input
                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      type="email"
                      autoComplete="email"
                      aria-required="true"
                      placeholder={t('inputs.emailPlaceholder')}
                      id={'email'}
                      key={'email'}
                      {...register('email', {
                        required: t('errors.fieldRequired'),
                      })}
                    />
                  </div>
                  {errors.email && (
                    <div className="absolute mt-1 text-xs font-bold text-red-600">
                      {errors.email.message}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    {t('inputs.password')}
                  </label>
                  <div className="mt-2">
                    <div className="relative">
                      <input
                        className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        aria-required="true"
                        placeholder={t('inputs.passwordPlaceholder')}
                        id={'password'}
                        key={'password'}
                        {...register('password', {
                          required: t('errors.fieldRequired'),
                          validate: validatePassword,
                        })}
                      />
                      <span
                        className="absolute inset-y-0 right-0 flex cursor-pointer select-none items-center pr-2"
                        onMouseDown={() => setShowPassword(true)}
                        onMouseUp={() => setShowPassword(false)}
                        onMouseLeave={() => setShowPassword(false)}
                        onTouchStart={() => setShowPassword(true)}
                        onTouchEnd={() => setShowPassword(false)}
                      >
                        <HeroIcons.EyeIcon
                          className={classNames(
                            'h-5 w-5',
                            showPassword
                              ? 'text-neutralGray/50'
                              : 'text-neutralGray',
                          )}
                        />
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-neutralGray">
                      {t('rules.password')}
                    </p>
                  </div>
                  {errors.password && (
                    <div className="mt-1 text-xs font-bold text-red-600">
                      {errors.password.message}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    {t('inputs.passwordConfirm')}
                  </label>

                  <div className="relative mt-2">
                    <input
                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      aria-required="true"
                      placeholder={t('inputs.passwordConfirmPlaceholder')}
                      id={'passwordConfirm'}
                      key={'passwordConfirm'}
                      {...register('passwordConfirm', {
                        required: t('errors.fieldRequired'),
                        validate: validatePasswordConfirm,
                      })}
                    />
                    <span
                      className="absolute inset-y-0 right-0 flex cursor-pointer select-none items-center pr-2"
                      onMouseDown={() => setShowPassword(true)}
                      onMouseUp={() => setShowPassword(false)}
                      onMouseLeave={() => setShowPassword(false)}
                      onTouchStart={() => setShowPassword(true)}
                      onTouchEnd={() => setShowPassword(false)}
                    >
                      <HeroIcons.EyeIcon
                        className={classNames(
                          'h-5 w-5',
                          showPassword
                            ? 'text-neutralGray/50'
                            : 'text-neutralGray',
                        )}
                      />
                    </span>
                  </div>
                  {errors.passwordConfirm && (
                    <div className="mt-1 text-xs font-bold text-red-600">
                      {errors.passwordConfirm.message}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <div className="flex">
                    <div className="flex items-center">
                      <div className="mb-auto">
                        <input
                          id={'acceptCondition'}
                          key={'acceptCondition'}
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                          {...register('acceptConditions', {
                            required: t('errors.acceptConditions'),
                          })}
                        />
                      </div>

                      <label
                        htmlFor="acceptCondition"
                        className="ml-3 block cursor-pointer text-xs leading-6 text-gray-900"
                      >
                        {t('acceptCgAndPrivacy')}{' '}
                        <Link
                          className="text-primary"
                          target="_blank"
                          href={{
                            pathname: `${getLocalePathFromPathname(
                              process.env.NEXT_PUBLIC_WWW_BASEURL
                                ? `${process.env.NEXT_PUBLIC_WWW_BASEURL}/[locale]/terms-of-use`
                                : '',
                              locale,
                            )}`,
                          }}
                        >
                          {t('termsOfUse')}
                        </Link>{' '}
                        {t('and')}{' '}
                        <Link
                          className="text-primary"
                          target="_blank"
                          href={{
                            pathname: `${getLocalePathFromPathname(
                              process.env.NEXT_PUBLIC_WWW_BASEURL
                                ? `${process.env.NEXT_PUBLIC_WWW_BASEURL}/[locale]/privacy-policy`
                                : '',
                              locale,
                            )}`,
                          }}
                        >
                          {t('privacyPolicy')}
                        </Link>
                        .
                      </label>
                    </div>
                  </div>
                  {errors.acceptConditions && (
                    <div className="text-xs font-bold text-red-600">
                      {errors.acceptConditions.message}
                    </div>
                  )}
                </div>

                <div>
                  <button
                    className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    type="submit"
                  >
                    {t('buttons.submit')}
                  </button>
                  {submitError && (
                    <div
                      className="relative py-2 text-xs text-red-700"
                      role="alert"
                    >
                      {submitError}
                    </div>
                  )}
                </div>
              </form>

              <div className="mt-8">
                <p className="text-center text-sm text-gray-500">
                  {t('alreadyAMember')}{' '}
                </p>

                <Link
                  href={routes.login}
                  className="mx-auto flex w-fit items-center font-bold leading-6 text-indigo-600 hover:text-indigo-500"
                >
                  <div className="mx-auto flex items-center underline">
                    {t('buttons.login')}
                    <HeroIcons.UserIcon className="ml-1 h-5 w-5" />
                  </div>
                </Link>
              </div>
            </div>

            <div className="mt-12 flex items-center">
              <div className="mx-auto rounded-2xl bg-neutralWhite">
                <LanguageSelect />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default onlyGuest(RegisterPage)

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'register'])),
    },
  }
}
